/**
 * Single source of truth for MediTalk social presence.
 *
 * Used by:
 *  - Footer social row
 *  - Registration success "Follow for new studies" strip
 *  - /research/:slug empty-state follow strip
 *  - Per-study share buttons on /studies/:id
 *  - Organization JSON-LD `sameAs` is set inline in public/index.html
 *    (server-rendered for crawlers); keep that array in sync if any
 *    of the four URLs below ever change.
 */
export const SOCIAL_LINKS = [
  {
    id: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/MediTalk.world/",
    handle: "MediTalk.world",
  },
  {
    id: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/meditalk.world/",
    handle: "@meditalk.world",
  },
  {
    id: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@meditalk_",
    handle: "@meditalk_",
  },
  {
    id: "x",
    label: "X (Twitter)",
    href: "https://x.com/MediTalkWorld",
    handle: "@MediTalkWorld",
  },
];

export const TWITTER_HANDLE = "@MediTalkWorld";

/**
 * Append UTM params so PostHog can attribute follower clicks back to
 * the on-site placement. Idempotent: if the URL already has the same
 * param it gets overwritten cleanly.
 */
export function withUtm(href, { source, medium, campaign }) {
  try {
    const url = new URL(href);
    if (source) url.searchParams.set("utm_source", source);
    if (medium) url.searchParams.set("utm_medium", medium);
    if (campaign) url.searchParams.set("utm_campaign", campaign);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Build a share URL for the given platform. Returns null for platforms
 * that don't support web share intents (Instagram, TikTok).
 */
export function buildShareUrl(platform, { url, text, subject }) {
  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(text || "");
  const encSubject = encodeURIComponent(subject || text || "");
  switch (platform) {
    case "x":
      return `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}&via=MediTalkWorld`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`;
    case "whatsapp":
      // wa.me/?text=... works on both mobile (deep-links into the WhatsApp
      // app) and desktop (opens WhatsApp Web). Single share endpoint, no
      // separate mobile vs desktop branch needed.
      return `https://wa.me/?text=${encText}%20${encUrl}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`;
    case "reddit":
      // Reddit's submit page pre-fills the title + URL; user picks the
      // subreddit themselves. Important for patient communities like
      // r/Narcolepsy, r/BreastCancer, etc.
      return `https://www.reddit.com/submit?url=${encUrl}&title=${encText}`;
    case "email":
      // mailto: , the most cross-generational share path. Caregivers
      // email parents and spouses constantly. Body includes the URL.
      return `mailto:?subject=${encSubject}&body=${encText}%0A%0A${encUrl}`;
    case "sms":
      // sms:?body=... deep-links to the texting app on iOS + Android
      // with the message pre-filled. Single biggest driver in older /
      // chronic-illness audiences who chat about studies via text.
      return `sms:?body=${encText}%20${encUrl}`;
    default:
      return null;
  }
}
